import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import {
  Building2,
  Users,
  FolderKanban,
  Plus,
  ChevronRight,
  ArrowLeft,
  X,
  AlertCircle,
  FileText,
  Layers,
  Calendar
} from 'lucide-react';
import { useEffect, useState } from 'react';

interface OrgDetail {
  id: string;
  name: string;
  slug: string;
  status: string;
  settings: Record<string, unknown>;
  memberCount: number;
  projectCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Project {
  id: string;
  name: string;
  key: string;
  description: string | null;
  status: string;
  epicCount?: number;
  requestCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface Member {
  id: string;
  role: string;
  joinedAt: string;
  user: {
    id: string;
    email: string;
    fullName: string | null;
    status: string;
  };
}

// ============================================
// New Project Modal
// ============================================

function NewProjectModal({
  orgId,
  onClose,
  onSuccess,
}: {
  orgId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: async () => {
      return api.post('/projects', {
        orgId,
        name,
        key: key.toUpperCase(),
        description: description || undefined,
      });
    },
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to create project');
    },
  });

  // Auto-generate key from name
  useEffect(() => {
    if (name && !key) {
      const words = name.split(/\s+/).filter(Boolean);
      if (words.length === 1) {
        setKey(words[0].slice(0, 4).toUpperCase());
      } else {
        setKey(words.map(w => w[0]).join('').slice(0, 5).toUpperCase());
      }
    }
  }, [name, key]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg animate-fade-in">
        <div className="card-header justify-between">
          <h3 className="card-title flex items-center gap-2">
            <FolderKanban className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            New Project
          </h3>
          <button onClick={onClose} className="btn btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="label">Project Name</label>
            <input
              type="text"
              className="input w-full"
              placeholder="e.g. Customer Portal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          
          <div>
            <label className="label">Project Key</label>
            <input
              type="text"
              className="input w-full uppercase"
              placeholder="e.g. CP"
              maxLength={10}
              value={key}
              onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            />
            <p className="text-xs text-muted mt-1">
              A short identifier for the project (max 10 chars, letters and numbers only)
            </p>
          </div>
          
          <div>
            <label className="label">Description (optional)</label>
            <textarea
              className="input w-full resize-none"
              rows={3}
              placeholder="Brief description of the project..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-danger/10 text-danger">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !name.trim() || !key.trim()}
            className="btn btn-primary"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function OrganizationDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { setHeader, clearHeader } = usePageHeaderStore();
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const queryClient = useQueryClient();
  
  const { data: org, isLoading: orgLoading } = useQuery({
    queryKey: ['org', orgId],
    queryFn: () => api.get<OrgDetail>(`/orgs/${orgId}`),
    enabled: !!orgId,
  });
  
  const { data: members } = useQuery({
    queryKey: ['org-members', orgId],
    queryFn: () => api.get<{ data: Member[] }>(`/orgs/${orgId}/members`),
    enabled: !!orgId,
  });
  
  const { data: projects } = useQuery({
    queryKey: ['org-projects', orgId],
    queryFn: () => api.get<{ data: Project[] }>(`/projects?orgId=${orgId}`),
    enabled: !!orgId,
  });
  
  // Set header when org data is loaded (must be before any early returns!)
  useEffect(() => {
    if (org) {
      setHeader(
        org.name,
        `@${org.slug}`,
        <Building2 className="w-5 h-5" style={{ color: 'var(--primary)' }} />,
        <span className={`badge ${org.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
          {org.status}
        </span>
      );
    }
    return () => clearHeader();
  }, [org, setHeader, clearHeader]);
  
  if (orgLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-1/4" />
        <div className="skeleton h-48 rounded-card" />
      </div>
    );
  }
  
  if (!org) {
    return (
      <div className="empty-state">
        <Building2 className="empty-state-icon" />
        <p className="empty-state-title">Organization not found</p>
        <p className="empty-state-text">The organization you're looking for doesn't exist</p>
        <Link to="/app/organizations" className="btn btn-secondary">
          <ArrowLeft className="w-4 h-4" />
          Back to Organizations
        </Link>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in">
      {/* Projects */}
      <div className="card mb-6">
        <div className="card-header justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ background: 'var(--primary-dim)' }}
            >
              <FolderKanban className="w-4 h-4" style={{ color: 'var(--primary)' }} />
            </div>
            <h3 className="card-title">Projects</h3>
          </div>
          <button className="btn btn-secondary text-sm" onClick={() => setShowNewProjectModal(true)}>
            <Plus className="w-4 h-4" />
            New Project
          </button>
        </div>
        
        <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
          {projects?.data.map((project, index) => (
            <Link
              key={project.id}
              to={`/app/organizations/${orgId}/projects/${project.id}`}
              className="block py-4 hover:bg-surface-2 -mx-5 px-5 transition-all rounded-md group"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start gap-3">
                <FolderKanban className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--primary)' }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <p className="font-medium text-default group-hover:text-primary transition-colors truncate">
                        {project.name}
                      </p>
                      <span className="text-xs text-muted font-mono flex-shrink-0">{project.key}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`badge ${project.status === 'active' ? 'badge-success' : 'badge-neutral'}`}>
                        {project.status}
                      </span>
                      <ChevronRight className="w-5 h-5 text-subtle group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                  {project.description && (
                    <p className="text-sm text-muted line-clamp-1 mt-1">{project.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted">
                    <span className="flex items-center gap-1">
                      <Layers className="w-3 h-3" />
                      {project.epicCount ?? 0} epics
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {project.requestCount ?? 0} stories
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(project.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
          {(!projects?.data || projects.data.length === 0) && (
            <div className="py-8 text-center">
              <FolderKanban className="w-10 h-10 mx-auto mb-3 text-subtle" />
              <p className="text-muted">No projects yet</p>
              <button className="btn btn-primary mt-4 text-sm" onClick={() => setShowNewProjectModal(true)}>
                <Plus className="w-4 h-4" />
                Create First Project
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Members */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ background: 'var(--secondary-dim)' }}
            >
              <Users className="w-4 h-4" style={{ color: 'var(--secondary)' }} />
            </div>
            <h3 className="card-title">Members</h3>
          </div>
        </div>
        
        <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
          {members?.data.map((member) => (
            <div key={member.id} className="flex items-center justify-between py-4">
              <div className="flex items-center gap-4">
                <div className="avatar avatar-md">
                  {member.user.fullName?.[0] || member.user.email[0].toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-default">
                    {member.user.fullName || member.user.email}
                  </p>
                  <p className="text-sm text-muted">{member.user.email}</p>
                </div>
              </div>
              <span className={`badge ${member.role === 'ORG_ADMIN' ? 'badge-primary' : 'badge-neutral'}`}>
                {member.role.replace('_', ' ')}
              </span>
            </div>
          ))}
          {(!members?.data || members.data.length === 0) && (
            <div className="py-8 text-center">
              <Users className="w-10 h-10 mx-auto mb-3 text-subtle" />
              <p className="text-muted">No members yet</p>
            </div>
          )}
        </div>
      </div>
      
      {/* New Project Modal */}
      {showNewProjectModal && orgId && (
        <NewProjectModal
          orgId={orgId}
          onClose={() => setShowNewProjectModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['org', orgId] });
            queryClient.invalidateQueries({ queryKey: ['org-projects', orgId] });
          }}
        />
      )}
    </div>
  );
}
